import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, Chip, Stack, Button, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { starAPI, userAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { downloadCsv } from '../../utils/exportCsv';
import { openTatItem } from '../../components/layout/TatPopup';

const TYPE_META = {
  project: { label: 'Project', color: '#1565C0' },
  defect: { label: 'Defect', color: '#C62828' },
  task: { label: 'Task', color: '#2E7D32' },
  ticket: { label: 'Ticket', color: '#6A1B9A' },
};

const daysBetween = (a, b) => Math.round((new Date(a + 'T00:00:00Z') - new Date(b + 'T00:00:00Z')) / 86400000);

export default function TatTrackerPage() {
  const navigate = useNavigate();
  const { isAdmin, isSupportTeam } = useAuth();
  const { showToast } = useToast();
  const canSeeAll = isAdmin() || isSupportTeam();
  const [when, setWhen] = useState('today');
  const [type, setType] = useState('');
  const [userId, setUserId] = useState('');
  const [users, setUsers] = useState([]);
  const [data, setData] = useState({ date: '', items: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (canSeeAll) userAPI.getAll().then((res) => setUsers(res.data)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { when };
    if (type) params.type = type;
    if (canSeeAll && userId) params.user_id = userId;
    starAPI.tatList(params)
      .then((res) => setData(res.data))
      .catch(() => showToast('Failed to load TAT list', 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [when, type, userId]);

  useEffect(() => { load(); }, [load]);

  const dueText = (i) => {
    if (!data.date) return '';
    const d = daysBetween(i.tat_date, data.date);
    if (i.done) return 'Finished';
    if (d === 0) return 'Due today';
    if (d < 0) return `Overdue by ${-d} day${d === -1 ? '' : 's'}`;
    return `In ${d} day${d === 1 ? '' : 's'}`;
  };

  const handleDownload = () => {
    if (!data.items.length) return showToast('Nothing to download', 'error');
    downloadCsv(`tat-${when}-${data.date}.csv`,
      ['Type', 'Name', 'Project', 'Status', 'Assigned To', 'TAT Date', 'Due'],
      data.items.map((i) => [TYPE_META[i.type].label, i.name, i.project_name || '', (i.status || '').replace('_', ' '),
        i.assignees.map((a) => a.name).join(', '), i.tat_date, dueText(i)]));
  };

  return (
    <Box>
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.5px' }}>TAT Tracker</Typography>
        <Typography color="text.secondary" variant="body2" sx={{ mt: 0.3 }}>
          {canSeeAll ? 'Projects, defects, tasks and tickets by TAT date — for all team members' : 'Your projects, defects, tasks and tickets by TAT date'}
        </Typography>
      </Box>

      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ flexWrap: 'wrap', gap: 1.5 }}>
            <ToggleButtonGroup size="small" exclusive value={when} onChange={(_, v) => v && setWhen(v)}>
              <ToggleButton value="today">Due today</ToggleButton>
              <ToggleButton value="overdue">Overdue</ToggleButton>
              <ToggleButton value="upcoming">Upcoming</ToggleButton>
              <ToggleButton value="all">All</ToggleButton>
            </ToggleButtonGroup>
            <TextField select size="small" label="Type" value={type} onChange={(e) => setType(e.target.value)} sx={{ minWidth: 150 }}>
              <MenuItem value="">All types</MenuItem>
              {Object.entries(TYPE_META).map(([k, m]) => <MenuItem key={k} value={k}>{m.label}</MenuItem>)}
            </TextField>
            {canSeeAll && (
              <TextField select size="small" label="Team member" value={userId} onChange={(e) => setUserId(e.target.value)} sx={{ minWidth: 200 }}>
                <MenuItem value="">All users</MenuItem>
                {users.map((u) => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
              </TextField>
            )}
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload} sx={{ ml: { md: 'auto' } }}>Download</Button>
          </Stack>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}><CircularProgress /></Box>
      ) : (
        <Card>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 800 }}>
              <TableHead>
                <TableRow sx={{ '& .MuiTableCell-head': { bgcolor: '#F4F7FB', fontWeight: 700, color: '#1E3A5F', fontSize: '0.75rem', whiteSpace: 'nowrap' } }}>
                  <TableCell>Type</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Assigned To</TableCell>
                  <TableCell>TAT Date</TableCell>
                  <TableCell>Due</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.items.map((i) => {
                  const meta = TYPE_META[i.type];
                  const names = i.assignees.map((a) => a.name).filter(Boolean);
                  const overdue = !i.done && i.tat_date < data.date;
                  return (
                    <TableRow key={`${i.type}-${i.id}`} hover sx={{ cursor: 'pointer', opacity: i.done ? 0.6 : 1 }} onClick={() => openTatItem(navigate, i)}>
                      <TableCell>
                        <Chip label={meta.label} size="small" sx={{ fontWeight: 700, bgcolor: meta.color + '18', color: meta.color }} />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 280 }}><Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{i.name}</Typography></TableCell>
                      <TableCell sx={{ maxWidth: 200 }}><Typography variant="body2" color="text.secondary" noWrap>{i.project_name || '—'}</Typography></TableCell>
                      <TableCell>
                        <Chip label={(i.status || '').replace('_', ' ')} size="small" variant="outlined" color={i.done ? 'success' : 'warning'} sx={{ textTransform: 'capitalize' }} />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Typography variant="body2" noWrap>
                          {names.length ? names.slice(0, 2).join(', ') + (names.length > 2 ? ` +${names.length - 2}` : '') : 'Unassigned'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{i.tat_date}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', color: overdue ? 'error.main' : 'text.primary', fontWeight: overdue ? 700 : 400 }}>{dueText(i)}</TableCell>
                    </TableRow>
                  );
                })}
                {data.items.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>Nothing here.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </Box>
  );
}
